/**
 * API client for the AI document intake job endpoints.
 *
 * Backend: Lender.Optimus.Ai — DEV-4424 / DEV-4527 / DEV-4555
 *
 * POST   document-intake-jobs                 → 202 { id, status }
 *        multipart/form-data: files[] (one part per file), loanApplicationId
 *        Accepts: PDFs, images (jpeg/png/tiff/heic/webp), ZIP archives.
 *        A single "file" field also accepted for backward-compat (legacy path).
 *
 * GET    document-intake-jobs/{id}            → { id, status, totalSegments,
 *        processedSegments, resultJson, errorMessage, createdDate,
 *        startedDate, completedDate }
 *
 * GET    document-intake-jobs?loanApplicationId=&mineOnly=&limit=
 *        → [{ id, status, … }]
 *
 * POST   document-intake-jobs/{id}/cancel     → 200
 *
 * DEV-4555:
 * GET    document-intake-jobs/{jobId}/sources/{sourceIndex}/content
 *        → streams application/pdf or image/* (authorized)
 *
 * POST   document-intake-jobs/{jobId}/segments/reclassify
 *        body { sourceIndex, startPageIndex, endPageIndex }
 *        → { matched, documentTypeId, confidence, reason }
 *
 * POST   document-intake-jobs/{id}/file-segments (extended)
 *        segments may now carry optional startPageIndex/endPageIndex/sourceIndex overrides.
 *        Response FiledIntakeSegmentResult per segment: { segmentIndex, succeeded, failureReason, documentId }
 */
import _ from 'lodash';

import { jsonParse } from 'utils/json';
import { logger } from 'utils/logger';

const safeParseResultJson = value => {
  if (!_.isString(value)) {
    return value ?? null;
  }

  try {
    return jsonParse(value);
  } catch (err) {
    logger.error('[aiDocumentIntakeApi] Failed to parse resultJson — blob may be truncated', err);

    return null;
  }
};

/**
 * Normalize a raw intake-job DTO from the backend (Lender.Optimus.Ai DEV-4424).
 * The service returns the identifier as `jobId` and serializes `resultJson` as a
 * JSON string; the whole client slice keys on `id` and reads `resultJson` as an
 * object, so reconcile both here at the single ingestion boundary.
 *
 * @param {object} raw
 * @returns {object}
 */
export const normalizeIntakeJob = raw => {
  if (!_.isObject(raw)) {
    return raw;
  }

  return {
    ...raw,
    id: raw.id ?? raw.jobId ?? null,
    // DEV-4552: sourceLabel is a backend-provided display name for the intake source
    // (e.g. filename, "3 files"). Defensive — may be absent on older backend versions.
    sourceLabel: raw.sourceLabel ?? null,
    // Count of segments whose classify call errored — null = plan not ready,
    // 0 = clean. Read null-safely so the FE works with or without the field.
    // (The unrecognized count is derived live from the segment list instead,
    // so it shrinks as the user assigns types.)
    failedSegments: raw.failedSegments ?? null,
    resultJson: safeParseResultJson(raw.resultJson),
  };
};

export class AiDocumentIntakeApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  /**
   * Submit one or more source files for background intake. Returns 202 immediately.
   * The backend may return an existing active job's id (dedup).
   *
   * DEV-4527: sends each file as a separate `files` part (multipart). The backend
   * controller accepts both the legacy `file` (single) and `files` (multi) field names
   * and merges them; we always use `files` for new submissions.
   *
   * @param {{ files: File[], loanApplicationId: string }} params
   * @returns {Promise<{ id: string, status: string }>}
   */
  async createJob({ files, loanApplicationId }) {
    const formData = new FormData();

    files.forEach(f => formData.append('files', f));
    formData.append('loanApplicationId', loanApplicationId);

    const { data } = await this.#http.post('document-intake-jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return normalizeIntakeJob(data);
  }

  /**
   * Poll a single job by id.
   * @param {{ id: string }} params
   */
  async getJob({ id }) {
    const { data } = await this.#http.get(`document-intake-jobs/${id}`);

    return normalizeIntakeJob(data);
  }

  /**
   * List jobs for a loan application.
   * @param {{ loanApplicationId?: string, mineOnly?: boolean, limit?: number }} params
   */
  async listJobs({ loanApplicationId, mineOnly, limit } = {}) {
    const params = {};

    if (loanApplicationId != null) {
      params.loanApplicationId = loanApplicationId;
    }

    if (mineOnly != null) {
      params.mineOnly = mineOnly;
    }

    if (limit != null) {
      params.limit = limit;
    }

    const { data } = await this.#http.get('document-intake-jobs', { params });

    return (data ?? []).map(normalizeIntakeJob);
  }

  /**
   * Cancel a running job.
   * @param {{ id: string }} params
   */
  async cancelJob({ id }) {
    await this.#http.post(`document-intake-jobs/${id}/cancel`);
  }

  /**
   * File specific segments from a completed background job.
   *
   * DEV-4555: each segment may now carry optional page-range / source overrides
   * for boundary-edited segments. The backend returns FiledIntakeSegmentResult
   * per segment: { segmentIndex, succeeded, failureReason, documentId }.
   *
   * @param {{
   *   id: string,
   *   segments: Array<{
   *     segmentIndex: number,
   *     documentTypeId: string,
   *     loanApplicationId: string,
   *     startPageIndex?: number,
   *     endPageIndex?: number,
   *     sourceIndex?: number,
   *   }>,
   * }} params
   * @returns {Promise<Array<{ segmentIndex: number, succeeded: boolean, failureReason: string|null, documentId: string|null }>>}
   */
  async fileSegments({ id, segments }) {
    const { data } = await this.#http.post(`document-intake-jobs/${id}/file-segments`, {
      segments,
    });

    return data ?? [];
  }

  /**
   * Stream the raw source file content for preview (DEV-4555).
   * Returns an ArrayBuffer of the PDF or image bytes so the caller can
   * either pass it to pdfjs `getDocument` or construct an object URL for `<img>`.
   *
   * @param {{ jobId: string, sourceIndex: number }} params
   * @returns {Promise<{ data: ArrayBuffer, contentType: string }>}
   */
  async getSourceContent({ jobId, sourceIndex }) {
    const response = await this.#http.get(
      `document-intake-jobs/${jobId}/sources/${sourceIndex}/content`,
      { responseType: 'arraybuffer' },
    );

    return {
      data: response.data,
      contentType: response.headers['content-type'] ?? 'application/pdf',
    };
  }

  /**
   * Re-classify a segment's page range (DEV-4555).
   * Called after boundary edits to refresh the AI suggestion before filing.
   *
   * @param {{
   *   jobId: string,
   *   sourceIndex: number,
   *   startPageIndex: number,
   *   endPageIndex: number,
   * }} params
   * @returns {Promise<{ matched: boolean, documentTypeId: string|null, confidence: number|null, reason: string|null }>}
   */
  async reclassifySegment({ jobId, sourceIndex, startPageIndex, endPageIndex }) {
    const { data } = await this.#http.post(`document-intake-jobs/${jobId}/segments/reclassify`, {
      sourceIndex,
      startPageIndex,
      endPageIndex,
    });

    return data ?? { matched: false, documentTypeId: null, confidence: null, reason: null };
  }
}
